import React, { useState } from 'react';
import styled from 'styled-components';
import Big from 'big.js';
import { BodyText, Button, FlexColumn, FlexRow, Input, SubtitleText } from '@make-software/cspr-design';
import { PaymentAmount, TransactionPayment } from '@/components';

interface BuyStampsFormProps {
  onSubmit: (amount: string) => void;
}

export const FormTitle = styled(SubtitleText)(({ theme }) =>
  theme.withMedia({
    fontWeight: [600, 600, 700],
    fontSize: ['20px', '24px', '24px'],
  }),
);

export const StyledInput = styled(Input)<{ withBorder?: boolean }>(({ theme, withBorder }) =>
  theme.withMedia({
    width: '100%',
    'div:nth-child(2)': {
      border: withBorder ? `1px solid ${theme.styleguideColors.contentSecondary}` : 'none'
    }
  })
);

export const StyledButton = styled(Button)(({ theme }) =>
  theme.withMedia({
    cursor: 'pointer'
  })
);

export const BuyStampsForm: React.FC<BuyStampsFormProps> = ({ onSubmit }) => {
  const [amount, setAmount] = useState<string>('');
  const [formErrors, setFormErrors] = useState<Record<'amount', string>>({
    amount: '',
  });

  const handleFillAmount = (value: string) => {
    setAmount(value);
    setFormErrors((prev) => ({ ...prev, amount: '' }));
  };

  const handleAmount = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;

    setFormErrors((prev) => ({ ...prev, amount: '' }));

    if (val === '') {
      setAmount(val);
      return;
    }

    if (/^(?!0$)(?!0\.0*$)\d*?\d*$/.test(val)) {
      setAmount(val);
    }
  };

  const handleOnKeyDownAmount = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];

    if (allowedKeys.includes(event.key)) return;

    if (event.key === '.') {
      const value = (event.target as HTMLInputElement).value;
      if (value.includes('.') || value === '') {
        event.preventDefault();
      }
      return;
    }

    if (/[0-9]/.test(event.key)) return;

    event.preventDefault();
  };

  const handleSubmit = () => {
    const nextErrors: Record<'amount', string> = { amount: '' };

    if (!amount.length) {
      nextErrors.amount = 'This field is required';
    }

    if (nextErrors.amount) {
      setFormErrors(nextErrors);
      return;
    }

    onSubmit(amount);
  };

  const hasErrors = Boolean(formErrors.amount);
  const isIncomplete = !amount.length;

  const cost = new Big(amount || 0)
    .mul(config.stamp_cost)
    .mul(1000000000);

  const transactionPayment = new Big(config.stamp_transaction_cost)
    .mul(1000000000);

  const total = cost.add(transactionPayment);

  return (
    <FlexColumn gap={25}>
      <FormTitle size={1} scale='lg' variation='black'>
        Buy Stamps
      </FormTitle>

      <FlexRow>
        <StyledInput
          value={amount}
          label={
            <BodyText size={1} variation="black">
              Stamps
            </BodyText>
          }
          placeholder="How many stamps do you want to buy?"
          onChange={handleAmount}
          onKeyDown={handleOnKeyDownAmount}
          error={!!formErrors.amount}
          validationText={formErrors.amount}
        />
      </FlexRow>

      <FlexRow gap={12}>
        <StyledButton onClick={() => handleFillAmount('5')}>5</StyledButton>
        <StyledButton onClick={() => handleFillAmount('25')}>25</StyledButton>
        <StyledButton onClick={() => handleFillAmount('50')}>50</StyledButton>
      </FlexRow>

      <PaymentAmount
        category={`Cost (1 Stamp = ${config.stamp_cost} CSPR)`}
        amount={cost.toString()}
        withDivider={true}
        canProceed={!isIncomplete}
      />

      <TransactionPayment
        amount={transactionPayment.toString()}
        withDivider={false}
        canProceed={!isIncomplete}
      />

      <PaymentAmount
        category="Total"
        amount={total.toString()}
        withDivider={true}
        canProceed={!isIncomplete}
      />

      <FlexRow>
        <StyledButton color="primaryRed" disabled={hasErrors || isIncomplete} onClick={handleSubmit}>
          Buy
        </StyledButton>
      </FlexRow>
    </FlexColumn>
  );
};
